import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  EmploymentSearchResultDto,
  IncidentIdResult,
  RoleIncidentRequest,
  RoleViewModel,
  SmartPortalProcessError,
  UserAdministrationClient,
} from '@core/api';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

export interface AssignUserRoleDialogData {
  employee: EmploymentSearchResultDto;
  roles: RoleViewModel[];
  isRequestForOwnUser: boolean;
  /**
   * Telekom-Mitarbeitende bekommen ihre Rollen unbefristet. Der Zeitraum wird
   * fuer sie weder abgefragt noch mitgeschickt, nur Externe werden befristet.
   */
  isTelekomEmployee: boolean;
}

export interface AssignUserRoleDialogResult {
  roles: RoleViewModel[];
  validFrom: Date;
  validUntil: Date;
}

@Component({
  selector: 'app-assign-user-role',
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    MatFormFieldModule,
    MatDialogModule,
    OverrideModule,
  ],
  templateUrl: './assign-user-role.component.html',
  styleUrl: './assign-user-role.component.scss',
})
export class AssignUserRoleComponent {
  form = new FormGroup({});

  // "Gueltig bis" ist mit einem Jahr im Voraus vorbelegt, der Vorschlag bleibt
  // aenderbar. Der Wert ist bewusst der Datumstyp des Adapters und kein Date:
  // der Picker laeuft mit useUtc und wuerde lokale Mitternacht als Vortag lesen.
  formModel: any = {
    validUntil: this.dateAdapter.addCalendarYears(this.dateAdapter.today(), 1),
  };

  step: 'form' | 'summary' = 'form';

  isLoading = false;

  formFields: FormlyFieldConfig[] = [
    {
      key: 'employeeInformation',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'access_orderer_representative.dialog.employee'
        ),
        readOnly: true,
        valueFormatter: () => {
          let employeeString: string = '';

          if (this.data.employee?.fullName) {
            employeeString += this.data.employee.fullName;
          }

          if (this.data.employee?.email) {
            employeeString += ', ' + this.data.employee.email;
          }

          return employeeString;
        },
      },
    },
    {
      key: 'validFrom',
      type: 'datepicker',
      className: 'validity-field',
      wrappers: ['width', 'form-field'],
      hide: this.data.isTelekomEmployee,
      props: {
        width: '250px',
        label: this.translateService.instant(
          'user.request.permissions.assignment.dialog.label__validFrom'
        ),
        datepickerOptions: {
          min: this.dateAdapter.today(),
        },
        required: true,
      },
    },
    {
      key: 'validUntil',
      type: 'datepicker',
      className: 'validity-field',
      wrappers: ['width', 'form-field'],
      hide: this.data.isTelekomEmployee,
      props: {
        width: '250px',
        label: this.translateService.instant(
          'user.request.permissions.assignment.dialog.label__validUntil'
        ),
        datepickerOptions: {
          // Fester Wert, nicht ueber expressionProperties nachgefuehrt: dieser
          // Weg kommt am Datepicker nicht an. Sperrt die Navigation in die
          // Vergangenheit.
          min: this.tomorrow(),
          // Der Filter traegt die eigentliche Regel und liest bei jedem Tag den
          // aktuellen Wert von "Gueltig von".
          filter: (date: unknown) => this.isSelectableValidUntil(date),
        },
        required: true,
      },
      validation: {
        messages: {
          matDatepickerMin: () => this.validityHintMessage(),
          matDatepickerFilter: () => this.validityHintMessage(),
        },
      },
    },
    {
      key: 'roles',
      type: 'autocomplete-chips-for-roles',
      props: {
        label: this.translateService.instant(
          'user.request.permissions.label__roles'
        ),
        required: true,
        options: this.data.roles,
      },
      validation: {
        messages: {
          required: this.translateService.instant('formErrors.required', {
            label: this.translateService.instant(
              'user.request.permissions.label__roles'
            ),
          }),
        },
      },
    },
  ];

  constructor(
    private translateService: TranslateService,
    private userAdministrationClient: UserAdministrationClient,
    private toastr: ToastrService,
    // Derselbe Adapter, den der Datepicker verwendet. Er laeuft laut
    // material-config.ts mit useUtc, deshalb darf hier nicht mit lokalen
    // Date-Objekten gerechnet werden: lokale Mitternacht ist in UTC noch der
    // Vortag und das Minimum landet einen Tag zu frueh.
    private dateAdapter: DateAdapter<unknown>,
    public dialogRef: MatDialogRef<AssignUserRoleComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssignUserRoleDialogData
  ) {}

  goToSummary() {
    if (this.form.valid) {
      this.step = 'summary';
    } else {
      this.form.markAllAsTouched();
    }
  }

  backToEdit() {
    this.step = 'form';
  }

  get selectedRoles(): RoleViewModel[] {
    return this.form.value['roles'] ?? [];
  }

  // Der Datepicker liefert Momente. Nach aussen und in die DatePipe der
  // Zusammenfassung gehen native Dates, die verarbeiten beide.
  get validFrom(): Date {
    return this.toNativeDate(this.rawValue('validFrom'));
  }

  get validUntil(): Date {
    return this.toNativeDate(this.rawValue('validUntil'));
  }

  private rawValue(key: 'validFrom' | 'validUntil'): unknown {
    return this.form.get(key)?.value;
  }

  /**
   * Wert als Datum des Adapters, oder null wenn er nicht auswertbar ist. Ein
   * Date wird ueber seine Kalenderbestandteile uebernommen und nicht ueber
   * deserialize: das rechnet den Zeitpunkt nach UTC um, wodurch lokale
   * Mitternacht auf dem Vortag landen wuerde.
   */
  private toDate(value: unknown): unknown | null {
    if (value instanceof Date) {
      return this.dateAdapter.createDate(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      );
    }

    return this.dateAdapter.getValidDateOrNull(
      this.dateAdapter.deserialize(value)
    );
  }

  /** Das im Kalender angezeigte Datum als lokales Date. */
  private toNativeDate(value: unknown): Date | undefined {
    const date = this.toDate(value);

    if (!date) {
      return undefined;
    }

    return new Date(
      this.dateAdapter.getYear(date),
      this.dateAdapter.getMonth(date),
      this.dateAdapter.getDate(date)
    );
  }

  /** Morgen im Datumstyp des Datepickers. */
  private tomorrow(): unknown {
    return this.dateAdapter.addCalendarDays(this.dateAdapter.today(), 1);
  }

  /**
   * Frueheste waehlbare Gueltigkeit. Eine Vergabe, die heute oder frueher
   * endet, waere bereits abgelaufen, und sie muss nach dem Beginn liegen.
   * Daher ist es der jeweils spaetere der beiden Folgetage.
   */
  private minValidUntil(): unknown {
    const tomorrow = this.tomorrow();
    const validFrom = this.toDate(this.rawValue('validFrom'));

    if (!validFrom) {
      return tomorrow;
    }

    const dayAfterValidFrom = this.dateAdapter.addCalendarDays(validFrom, 1);

    return this.dateAdapter.compareDate(dayAfterValidFrom, tomorrow) > 0
      ? dayAfterValidFrom
      : tomorrow;
  }

  private isSelectableValidUntil(value: unknown): boolean {
    const date = this.toDate(value);

    // Leere Tage im Kalenderraster reicht Material ebenfalls durch.
    if (!date) {
      return true;
    }

    return this.dateAdapter.compareDate(date, this.minValidUntil()) >= 0;
  }

  /**
   * Begruendung fuer ein abgelehntes "Gueltig bis". Der Kalender sperrt diese
   * Tage bereits, ueber die Tastatur laesst sich aber jedes Datum eintragen.
   */
  private validityHintMessage(): string {
    const validUntil = this.toDate(this.rawValue('validUntil'));
    const validFrom = this.toDate(this.rawValue('validFrom'));

    const beforeValidFrom =
      validFrom &&
      validUntil &&
      this.dateAdapter.compareDate(validUntil, validFrom) <= 0;

    return this.translateService.instant(
      beforeValidFrom
        ? 'user.request.permissions.assignment.dialog.error__validUntil_before_validFrom'
        : 'user.request.permissions.assignment.dialog.error__validUntil_not_in_future'
    );
  }

  private toDateOnlyString(value: Date): string | undefined {
    return value ? moment(value).format('YYYY-MM-DD') : undefined;
  }

  submitRequest(): void {
    this.isLoading = true;

    const roleIds = this.selectedRoles.map(role => role.id);
    const validFrom = this.toDateOnlyString(this.validFrom);
    const validUntil = this.toDateOnlyString(this.validUntil);

    let requestObsv: Observable<IncidentIdResult>;

    if (this.data.isRequestForOwnUser) {
      const request: RoleIncidentRequest = {
        roles: roleIds,
        validFrom: validFrom as unknown as Date,
        validUntil: validUntil as unknown as Date,
      };

      requestObsv = this.userAdministrationClient.requestOwnRoles(request);
    } else {
      const request: RoleIncidentRequest = {
        email: this.data.employee.email,
        roles: roleIds,
        validFrom: validFrom as unknown as Date,
        validUntil: validUntil as unknown as Date,
      };

      requestObsv = this.userAdministrationClient.requestRoles(request);
    }

    requestObsv.subscribe({
      next: () => {
        this.isLoading = false;

        const result: AssignUserRoleDialogResult = {
          roles: this.selectedRoles,
          validFrom: this.validFrom,
          validUntil: this.validUntil,
        };

        this.dialogRef.close(result);
      },
      error: error => {
        this.isLoading = false;

        let errorMessage: string;

        if (
          error.errorCode ===
          SmartPortalProcessError.ROLES_ALREADY_ASSIGNED_OR_REQUESTED
        ) {
          errorMessage = this.translateService.instant(
            'user.request.permissions.error_roles_assigned_or_requested'
          );
        } else if (
          error.errorCode === SmartPortalProcessError.ROLES_ALREADY_REQUESTED
        ) {
          errorMessage = this.translateService.instant(
            'user.request.permissions.error_roles_requested'
          );
        } else {
          errorMessage = this.translateService.instant(
            'user.request.permissions.errorMessage'
          );
        }

        this.toastr.error(errorMessage);
      },
    });
  }
}
